"use client";

import { pxToRem } from "@ui/lib/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Select } from "../../Select";
import { clsx } from "clsx";
import { useTranslations } from "next-intl";
import { type Key } from "react-aria-components";
import IconSquareBehindSquare4 from "@ui/central-icons/icons/IconSquareBehindSquare4";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { anchoredToastManager } from "@/app/_providers/AnchoredToastProvider";
import { LimitReachedOverlay } from "../../LimitReachedOverlay";
import { appUrls } from "@/constants";
import { Footer } from "../../Footer";
import type { CodeToken } from "@ui/lib/syntaxHighlighting";

export const inputClassName = ({
  isActive = false,
  isDisabled = false,
  useFocusVisible = false,
}: {
  isActive?: boolean;
  isDisabled?: boolean;
  useFocusVisible?: boolean;
}) =>
  clsx(
    "tw-rounded tw-ring-0.5 tw-ring-inset tw-outline-none",
    useFocusVisible
      ? "focus-visible:tw-ring-blue-500 focus-visible:tw-ring-1"
      : "focus-within:tw-ring-blue-500 focus-within:tw-ring-1",
    isDisabled
      ? "tw-bg-gray-50 tw-ring-gray-200 tw-text-gray-600"
      : ["tw-bg-blue-50/50", isActive ? "tw-ring-blue-500" : "tw-ring-blue-300 hover:tw-ring-blue-500"],
  );

export function TextInput({
  value,
  onChange,
  isDisabled = false,
  "aria-label": ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  isDisabled?: boolean;
  "aria-label": string;
}) {
  const scrollRef = useRef<HTMLSpanElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  const updateScrollState = useCallback(() => {
    if (!scrollRef.current) return;
    setAtStart(Math.round(scrollRef.current.scrollLeft) <= 0);
    setAtEnd(
      Math.round(scrollRef.current.scrollLeft + scrollRef.current.clientWidth) >=
        Math.round(scrollRef.current.scrollWidth),
    );
  }, []);

  useEffect(() => {
    if (!scrollRef.current) return;
    const observer = new ResizeObserver(() => {
      updateScrollState();
    });
    observer.observe(scrollRef.current);
    return () => {
      observer.disconnect();
    };
  }, [updateScrollState]);

  return (
    <span
      className={clsx(
        "tw-group tw-relative tw-flex-auto tw-flex tw-min-w-0 tw-h-6 tw-self-center",
        inputClassName({ isDisabled }),
      )}
    >
      <span
        className={clsx(
          "tw-relative tw-min-w-0 tw-flex-auto tw-h-6 tw-overflow-x-auto tw-overscroll-contain tw-flex tw-scrollbar-none tw-leading-6",
        )}
        onScroll={() => {
          updateScrollState();
        }}
        ref={scrollRef}
      >
        <span className='tw-relative tw-px-1.5 tw-overflow-hidden tw-flex-none tw-flex'>
          <span className='tw-invisible tw-flex-none'>{value || " "}</span>
          <input
            type='text'
            value={value}
            placeholder=' '
            onChange={e => onChange(e.target.value)}
            className={clsx(
              "tw-absolute tw-inset-0 tw-inset-x-1.5 tw-h-6 tw-w-[calc(100%-0.375rem)] tw-caret-focus tw-bg-transparent tw-outline-none tw-border-0",
            )}
            disabled={isDisabled}
            spellCheck={false}
            maxLength={500}
            aria-label={ariaLabel}
          />
        </span>
      </span>

      {/* Overflow fade left */}
      <span
        className={clsx(
          "tw-pointer-events-none tw-bg-white tw-w-8 tw-absolute tw-inset-y-0 tw-left-0 tw-rounded-l tw-border-l-0.5 tw-border-y-0.5 tw-overflow-hidden tw-transition-opacity tw-duration-300",
          atStart ? "tw-opacity-0" : "tw-delay-100",
          isDisabled
            ? "tw-border-gray-200"
            : "tw-border-blue-300 group-focus-within:tw-border-blue-500 group-focus-within:tw-border-l group-focus-within:tw-border-y group-hover:tw-border-blue-500",
        )}
        style={{ maskImage: "linear-gradient(to left, transparent, white)" }}
      >
        <span className={clsx("tw-absolute tw-inset-0", isDisabled ? "tw-bg-gray-50" : "tw-bg-blue-50/50")} />
      </span>
      {/* Overflow fade right */}
      <span
        className={clsx(
          "tw-pointer-events-none tw-bg-white tw-w-8 tw-absolute tw-inset-y-0 tw-right-0 tw-rounded-r tw-border-r-0.5 tw-border-y-0.5 tw-overflow-hidden tw-transition-opacity tw-duration-300",
          atEnd ? "tw-opacity-0" : "tw-delay-100",
          isDisabled
            ? "tw-border-gray-200"
            : "tw-border-blue-300 group-focus-within:tw-border-blue-500 group-focus-within:tw-border-l group-focus-within:tw-border-y group-hover:tw-border-blue-500",
        )}
        style={{ maskImage: "linear-gradient(to right, transparent, white)" }}
      >
        <span className={clsx("tw-absolute tw-inset-0", isDisabled ? "tw-bg-gray-50" : "tw-bg-blue-50/50")} />
      </span>
    </span>
  );
}

function CopyButton({ text }: { text: string }) {
  const tOmnibox = useTranslations("ClientKeys.BlockInteractCreateOmnibox");
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [copied, setCopied] = useState(false);

  return (
    <button
      ref={buttonRef}
      type='button'
      aria-label={tOmnibox("api.copy_code")}
      className={clsx(
        "tw-size-9 tw-flex-none tw-text-gray-500 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-colors tw-outline-none focus-visible:tw-outline-focus",
        copied ? "" : "hover:tw-text-black",
      )}
      disabled={copied}
      onClick={() => {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            setCopied(true);
            anchoredToastManager.add({
              description: tOmnibox("api.copied"),
              timeout: 1500,
              positionerProps: {
                anchor: buttonRef.current,
              },
              onClose() {
                setCopied(false);
              },
            });
          })
          .catch(() => {});
      }}
    >
      <IconSquareBehindSquare4 className={clsx("tw-size-4", iconStrokeFix)} />
    </button>
  );
}

export function APIDemo({
  state,
  code,
  codeLanguage,
  onChangeCodeLanguage,
  showLimitReachedOverlay,
  onCloseLimitReachedOverlay,
  limitReachedOverlayEventProps,
  copyText,
  primaryButtonIsDisabled,
  onPrimaryButtonClick,
  tokenClassName,
  renderCodePlaceholder,
  footerProps,
  secondaryButton,
  scrollKey,
  showCodeLanguageSelect = true,
}: {
  state: "idle" | "loading" | "playing" | "paused";
  codeLanguage: Key;
  onChangeCodeLanguage: (value: Key | null) => void;
  showLimitReachedOverlay: boolean;
  onCloseLimitReachedOverlay: () => void;
  limitReachedOverlayEventProps?: Record<string, unknown>;
  copyText: string;
  primaryButtonIsDisabled: boolean;
  onPrimaryButtonClick: () => void;
  tokenClassName?: (token: { content: string; color?: string }) => string;
  renderCodePlaceholder?: (name: string, { isActive }: { isActive: boolean }) => React.ReactNode;
  code: {
    ts: { code: string; quotes: "single" | "double"; tokens: CodeToken[][] };
    python: { code: string; quotes: "single" | "double"; tokens: CodeToken[][] };
  };
  footerProps?: React.ComponentProps<typeof Footer>;
  secondaryButton?: React.ReactNode;
  showCodeLanguageSelect?: boolean;
  scrollKey?: string | number;
}) {
  const t = useTranslations("ClientKeys.Globals");
  const tTTS = useTranslations("ClientKeys.TTS");
  const tOmnibox = useTranslations("ClientKeys.BlockInteractCreateOmnibox");
  const [isActive, setIsActive] = useState(true);

  const codeLanguageItems = useMemo(
    () => [
      { id: "ts", textValue: "TypeScript" },
      { id: "python", textValue: "Python" },
    ],
    [],
  );

  return (
    <div className='tw-h-full tw-flex tw-items-center'>
      <div
        className='-tw-mt-9 tw-relative tw-isolate tw-bg-white tw-rounded-3xl tw-w-full tw-mx-auto tw-ring-0.5 tw-ring-inset tw-ring-gray-200'
        style={{ maxWidth: pxToRem(640) }}
      >
        <div
          key={scrollKey}
          className='tw-overflow-auto tw-scroll-pt-4 tw-scroll-pb-6 tw-h-[17rem] tw-flex tw-flex-col tw-scrollbar-none'
          inert={showLimitReachedOverlay}
        >
          <pre className='tw-relative tw-flex-auto tw-pl-5 tw-pt-3.5'>
            <code className='tw-font-mono tw-text-2xs/[1.625rem]'>
              {code[codeLanguage as keyof typeof code].tokens.map((line, lineIndex) => (
                <span key={lineIndex} className='tw-flex tw-min-w-0'>
                  {line.map((token, tokenIndex) => (
                    <span
                      key={tokenIndex}
                      style={{ color: token[1] }}
                      className={clsx("tw-whitespace-pre", tokenClassName?.({ content: token[0], color: token[1] }))}
                    >
                      {token[0].includes("__")
                        ? token[0].split(/__([A-Z_]+)__/).map((part, partIndex) => {
                            if (partIndex % 2 === 0) {
                              return part;
                            }
                            return renderCodePlaceholder?.(part, { isActive });
                          })
                        : token[0]}
                    </span>
                  ))}
                  <span className='tw-flex-none tw-w-5' style={{ height: pxToRem(26) }} />
                  {"\n"}
                </span>
              ))}
            </code>
          </pre>
          <div className='tw-flex-none tw-sticky tw-bottom-0 tw-h-6 tw-bg-gradient-to-t tw-from-white tw-mx-[0.5px]' />
        </div>
        <div className='tw-relative tw-z-10 tw-flex tw-items-center tw-px-3 tw-pb-3' inert={showLimitReachedOverlay}>
          <CopyButton text={copyText} />
          {showCodeLanguageSelect && (
            <Select
              items={codeLanguageItems}
              value={codeLanguage}
              onChange={onChangeCodeLanguage}
              aria-label={tTTS("widget.language")}
            />
          )}
          {isActive && (
            <div className='tw-flex tw-gap-2 tw-ml-auto'>
              {secondaryButton}
              <button
                type='button'
                className={clsx(
                  "tw-flex-none tw-bg-black tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-colors tw-outline-none focus-visible:tw-outline-focus tw-text-sm/none",
                  primaryButtonIsDisabled ? "tw-cursor-not-allowed" : "hover:tw-bg-gray-600",
                  state === "loading" ? "tw-size-9" : "tw-h-9 tw-px-3.5",
                )}
                onClick={onPrimaryButtonClick}
                aria-label={
                  state === "playing"
                    ? t("pause")
                    : state === "paused"
                      ? t("play")
                      : state === "loading"
                        ? t("loading")
                        : undefined
                }
                disabled={primaryButtonIsDisabled || !isActive}
              >
                {state === "loading" ? (
                  <svg
                    viewBox='0 0 24 24'
                    aria-hidden
                    className='tw-size-4 tw-animate-spin'
                    style={{ animationDuration: "0.7s" }}
                  >
                    <g fill='none' stroke='currentColor' strokeWidth='2.25'>
                      <path strokeOpacity='.3' d='M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z' />
                      <path strokeLinecap='round' d='M20.945 13A9.004 9.004 0 0 1 13 20.945' />
                    </g>
                  </svg>
                ) : state === "idle" ? (
                  <>
                    <span className='tw-contents sm:tw-hidden'>{tOmnibox("api.run_code_short")}</span>
                    <span className='tw-hidden sm:tw-contents'>{tOmnibox("api.run_code")}</span>
                  </>
                ) : state === "playing" ? (
                  t("pause")
                ) : (
                  t("play")
                )}
              </button>
            </div>
          )}
        </div>
        {footerProps && <Footer {...footerProps} />}
        <LimitReachedOverlay
          show={showLimitReachedOverlay}
          ctaLink={appUrls.apiKeys}
          title={tTTS("widget.quota_exceeded_title")}
          description={tTTS("widget.quota_exceeded_description")}
          eventProperties={{
            limit_type: "quota",
            ...limitReachedOverlayEventProps,
          }}
          onClose={() => {
            setIsActive(false);
            onCloseLimitReachedOverlay();
          }}
        />
      </div>
    </div>
  );
}
